import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Lottie from 'lottie-react';
import compassAnimatedData from '../../assets/lottie/compass_animated.json';
import CountingNumber from '../CountingNumber';
import { scrollToAppPromotion } from '../../utils/scrollToApp';
import { getRankFromElo } from '../../utils/rankingUtils';

interface ChartDataPoint {
  date: number;
  dateLabel: string;
  elo: number;
  fullDate: string;
}

interface PlayerEloHistoryProps {
  chartData: ChartDataPoint[];
  isLikelyFemale: boolean;
  currentLang: 'en' | 'fr' | 'de' | 'it';
  t: {
    eloHistory: string;
    games: string;
    months: string;
    viewAll: string;
  };
}

/**
 * PlayerEloHistory Component
 * Displays ELO history chart with compass Lottie animation and stats
 */
const PlayerEloHistory: React.FC<PlayerEloHistoryProps> = ({
  chartData,
  isLikelyFemale,
  currentLang,
  t
}) => {
  if (chartData.length === 0) return null;

  // Aggregate data by day
  const aggregatedData: any[] = [];
  const dataByDate = new Map();

  chartData.forEach(point => {
    const dateKey = new Date(point.date).toDateString();
    if (!dataByDate.has(dateKey)) {
      dataByDate.set(dateKey, []);
    }
    dataByDate.get(dateKey).push(point);
  });

  // For each day, take the last match's ELO and count wins/losses
  dataByDate.forEach((matches, dateKey) => {
    const lastMatch = matches[matches.length - 1];
    const wins = matches.filter((m: any, i: number) =>
      i === 0 || m.elo > matches[i - 1].elo
    ).length;
    const losses = matches.length - wins;

    aggregatedData.push({
      ...lastMatch,
      matchCount: matches.length,
      wins,
      losses,
      dateKey
    });
  });

  // Translations for chart stats
  const chartTranslations = {
    en: { peak: 'Peak', current: 'Current', change: 'Total Change' },
    fr: { peak: 'Maximum', current: 'Actuel', change: 'Changement Total' },
    de: { peak: 'Höchstwert', current: 'Aktuell', change: 'Gesamtänderung' },
    it: { peak: 'Massimo', current: 'Attuale', change: 'Variazione Totale' }
  };
  const chartT = chartTranslations[currentLang];

  const thisMonthTranslations = {
    en: 'This month',
    fr: 'Ce mois',
    de: 'Dieser Monat',
    it: 'Questo mese'
  };

  return (
    <motion.div
      className="max-w-6xl mx-auto mb-16"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 1.0 }}>

      {/* Section Header - Standardized */}
      <motion.div className="text-center mb-10">
        {/* Lottie above title */}
        <motion.div
          className="flex justify-center mb-4"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 1.1 }}
        >
          <div style={{ width: '240px', height: '240px' }}>
            <Lottie
              animationData={compassAnimatedData}
              loop
              style={{ width: '100%', height: '100%' }}
              rendererSettings={{
                preserveAspectRatio: 'xMidYMid meet'
              }}
            />
          </div>
        </motion.div>
        <h2 className="text-4xl font-light text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-300">{t.eloHistory}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
          <CountingNumber target={chartData.length} duration={1.5} delay={450} decimals={0} suffix={` ${t.games}`} /> • {(() => {
            const firstDate = new Date(chartData[0].date);
            const lastDate = new Date(chartData[chartData.length - 1].date);
            const months = Math.round((lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
            return months > 0 ? `${months} ${t.months}` : thisMonthTranslations[currentLang];
          })()}
        </p>
      </motion.div>

      {/* Simplified Chart Container - Mobile optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-none md:rounded-2xl p-0 md:p-8 shadow-none md:shadow-sm border-0 md:border md:border-gray-100 dark:md:border-gray-700 transition-colors duration-300">
        {/* Minimal Stats Bar - Mobile responsive */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100 dark:border-gray-700 px-4 md:px-0 mt-4 md:mt-0 transition-colors duration-300">
          <div className="flex gap-6 md:gap-8">
            <div>
              <div className="flex items-baseline gap-2">
                <div className="text-xl md:text-2xl font-light text-gray-900 dark:text-gray-100 transition-colors duration-300">
                  {getRankFromElo(Math.max(...chartData.map(d => d.elo)), isLikelyFemale ? 'F' : 'M')}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
                  ({Math.round(Math.max(...chartData.map(d => d.elo)))})
                </div>
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-1 transition-colors duration-300">{chartT.peak}</div>
            </div>
            <div>
              <div className="flex items-baseline gap-2">
                <div className="text-xl md:text-2xl font-light text-gray-900 dark:text-gray-100 transition-colors duration-300">
                  {getRankFromElo(chartData[chartData.length - 1].elo, isLikelyFemale ? 'F' : 'M')}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
                  ({Math.round(chartData[chartData.length - 1].elo)})
                </div>
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-1 transition-colors duration-300">{chartT.current}</div>
            </div>
            <div className="hidden sm:block">
              <div className="text-xl md:text-2xl font-light text-gray-900 dark:text-gray-100 transition-colors duration-300">
                {(() => {
                  const diff = Math.round(chartData[chartData.length - 1].elo - chartData[0].elo);
                  return `${diff >= 0 ? '+' : ''}${diff}`;
                })()}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-1 transition-colors duration-300">{chartT.change}</div>
            </div>
          </div>
        </div>

        {/* Clean Chart - No margins on mobile */}
        <div className="w-full">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={aggregatedData} margin={{
              top: 10,
              right: 10,
              bottom: 30,
              left: 5
            }}>
              {/* Minimal grid */}
              <CartesianGrid strokeDasharray="0" stroke="#f3f4f6" vertical={false} />

              {/* X Axis - cleaner */}
              <XAxis
                dataKey="date"
                type="number"
                domain={['dataMin', 'dataMax']}
                tick={{ fontSize: 9, fill: '#9ca3af' }}
                stroke="#e5e7eb"
                axisLine={{ stroke: '#e5e7eb' }}
                tickFormatter={(timestamp) => {
                  const date = new Date(timestamp);
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }}
              />

              {/* Y Axis - with rank classifications */}
              <YAxis
                domain={[(dataMin: number) => Math.floor(dataMin / 50) * 50,
                         (dataMax: number) => Math.ceil(dataMax / 50) * 50]}
                tick={{ fontSize: 9, fill: '#9ca3af' }}
                stroke="#e5e7eb"
                axisLine={{ stroke: '#e5e7eb' }}
                width={45}
                tickFormatter={(value) => {
                  const rank = getRankFromElo(value, isLikelyFemale ? 'F' : 'M');
                  return `${rank}`;
                }}
              />

            {/* Simple Tooltip */}
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '11px',
                padding: '8px'
              }}
              formatter={(value: any, _name: string) => {
                const roundedValue = Math.round(value);
                const rank = getRankFromElo(roundedValue, isLikelyFemale ? 'F' : 'M');
                const point = aggregatedData.find((d: any) => Math.round(d.elo) === roundedValue);
                if (point?.matchCount > 1) {
                  return [`${rank} (${roundedValue}) - ${point.matchCount} ${t.games}: ${point.wins}W/${point.losses}L`, ''];
                }
                return [`${rank} (${roundedValue})`, ''];
              }}
              labelFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              }}
            />

            {/* Single clean line */}
            <Line
              type="monotone"
              dataKey="elo"
              stroke="#1C7AD6"
              strokeWidth={2}
              dot={(props: any) => {
                const { cx, cy, index, key } = props;
                const point = aggregatedData[index];

                // Only show dots for days with multiple matches
                if (point?.matchCount > 1) {
                  return (
                    <circle
                      key={key || `dot-${index}`}
                      cx={cx}
                      cy={cy}
                      r="5"
                      fill="#1C7AD6"
                      stroke="white"
                      strokeWidth="2"
                    />
                  );
                }

                // Small dot for single matches
                return (
                  <circle
                    key={key || `dot-${index}`}
                    cx={cx || 0}
                    cy={cy || 0}
                    r="2"
                    fill="#1C7AD6"
                  />
                );
              }}
              activeDot={{
                r: 6,
                fill: '#1C7AD6',
                stroke: 'white',
                strokeWidth: 2
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* View All Button */}
      <div className="mt-6 text-center">
        <button
          onClick={() => scrollToAppPromotion()}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-300"
        >
          {t.viewAll} →
        </button>
      </div>
    </div>
    </motion.div>
  );
};

export default PlayerEloHistory;
